"use client";

/**
 * useSceneView — fires `landing_scene_view` exactly once per session when a
 * landing scene crosses the 50% viewport visibility threshold.
 *
 * Wiring contract:
 *   - Each landing scene component calls `useSceneView({ sceneId, sceneName, ref })`
 *     once on mount.
 *   - The hook attaches a single IntersectionObserver(threshold: 0.5) to the
 *     scene element. Once `entry.isIntersecting && entry.intersectionRatio >= 0.5`
 *     fires, the observer disconnects and `trackEventSessionOnce` records the
 *     event (sessionStorage-scoped dedupe — survives Fast Refresh + StrictMode
 *     double-effect-fire + back/forward within the same tab).
 *
 * Why custom and not `react-intersection-observer`:
 *   - 10+ existing IntersectionObserver call sites in this codebase already
 *     write the native API inline (StickyNavRail, NicheGrid, blog ToC, niche-v3
 *     DecideScene, etc.) — adding a dep for one more usage would diverge from
 *     the established pattern.
 *   - The hook is ~30 lines including types; the dep would add ~3kB gzipped.
 *
 * SSR-safety:
 *   - `useEffect` only runs client-side; the guard `typeof window === "undefined"`
 *     inside `trackEventSessionOnce` covers any edge case.
 *
 * Fast-Refresh / StrictMode:
 *   - sessionStorage dedupe handles React 19 double-invoke in dev. The second
 *     effect mount sees the key already set and silently no-ops.
 *
 * Anti-pattern compliance:
 *   - No module-top-level const carrying control-flow values. The dedupe set
 *     is sessionStorage, owned by `trackEventSessionOnce`. No per-module Set
 *     instance shared across hook calls.
 */

import { useEffect, type RefObject } from "react";
import { trackEventSessionOnce } from "@/lib/analytics";

export type LandingSceneId = "0" | "1" | "2" | "4" | "5";

export interface UseSceneViewOptions {
  sceneId: LandingSceneId;
  /** Human label for the scene (e.g. "Mission", "What Survived"). */
  sceneName: string;
  /** Ref to the section element being observed. Mutually exclusive with `elementId`. */
  ref?: RefObject<HTMLElement | null>;
  /**
   * DOM id of the section to observe. Used when the section is owned by a
   * Server Component (no ref attachable in JSX). The hook resolves the
   * element via `document.getElementById` inside its effect.
   */
  elementId?: string;
  /** Intersection threshold (default 0.5). */
  threshold?: number;
}

export function useSceneView({
  sceneId,
  sceneName,
  ref,
  elementId,
  threshold = 0.5,
}: UseSceneViewOptions): void {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const el: HTMLElement | null = ref?.current
      ?? (elementId ? document.getElementById(elementId) : null);
    if (!el) return;
    // Older browsers without IntersectionObserver — feature-detect and bail.
    // intentional: bare guard, no Sentry — analytics is best-effort
    if (typeof IntersectionObserver === "undefined") return;

    let fired = false;
    const observer = new IntersectionObserver(
      (entries) => {
        if (fired) return;
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= threshold) {
            fired = true;
            observer.disconnect();
            trackEventSessionOnce(
              "landing_scene_view",
              `scene-${sceneId}`,
              { scene: sceneId, scene_name: sceneName },
            );
            break;
          }
        }
      },
      { threshold },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, elementId, sceneId, sceneName, threshold]);
}
